import type { ReactNode } from 'react';

/**
 * Brand-ish colors per platform so the operator can tell at a glance whether
 * something is about a Kalshi or a Polymarket order without reading the
 * platform word.
 *
 * Kalshi → emerald (their brand teal-green).
 * Polymarket → bright blue (their brand purple-blue).
 *
 * Lifted out of `NotificationsPage` so the triage tray shares the SAME
 * vocabulary instead of growing a second one — two screens that disagree on
 * what colour Kalshi is are worse than neither having colour at all. Colours,
 * not logos: the feed's summary lines stay text-only and copyable.
 */
export const PLATFORM_COLORS: Record<string, string> = {
  kalshi: '#00b894',
  polymarket: '#4a9eff',
};

export const PLATFORM_DISPLAY: Record<string, string> = {
  kalshi: 'Kalshi',
  polymarket: 'Poly',
};

/** The display name for a platform, or the raw value when it is unknown. */
export function platformLabel(platform: unknown): string {
  const key = String(platform ?? '').toLowerCase();
  return PLATFORM_DISPLAY[key] ?? (platform ? String(platform) : '?');
}

/** The brand colour, or `undefined` when the platform is unknown — an unknown
 *  platform renders in the ordinary text colour rather than a made-up one. */
export function platformColor(platform: unknown): string | undefined {
  return PLATFORM_COLORS[String(platform ?? '').toLowerCase()];
}

/**
 * YES / NO.
 *
 * Green-for-yes, red-for-no is already the app's vocabulary
 * (`UnwindingPage`, `UnwindingTabSumOfSides`), so it is reused rather than
 * replaced. These are DESATURATED against the MUI defaults on purpose: the
 * side is context, not an alarm, and at full saturation half a dozen of them
 * on one screen shout over the lines that are actually a warning.
 *
 * They do not collide with the platform hues even though Kalshi is also
 * green: a side renders as a bordered pill and a platform as a bare coloured
 * word, so the shapes carry the distinction the hues alone would not.
 */
export const SIDE_COLORS: Record<string, string> = {
  yes: '#6fcf97',
  no: '#e08a8a',
};

export function sideColor(side: unknown): string | undefined {
  return SIDE_COLORS[String(side ?? '').toLowerCase()];
}

export type PlatformNode = ReactNode;
